/**
 * Meta (Facebook) Pixel E-Commerce Event Tracking
 *
 * Fires standard Meta Pixel events for retargeting and conversion tracking.
 * All functions are safe to call even if fbq is not available, blocked by an
 * ad blocker, or throws: every call goes through fbqSafe(), so a tracking
 * failure can never break the cart, checkout or configurator.
 *
 * No personal data: events carry product ids/names, prices, order numbers and
 * page paths only. Never pass names, emails, phone numbers or addresses here.
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

function isFbqAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.fbq === 'function';
  } catch {
    return false;
  }
}

/** Call fbq without ever throwing. Returns false when fbq is missing or failed. */
function fbqSafe(...args: unknown[]): boolean {
  if (!isFbqAvailable()) return false;
  try {
    window.fbq!(...args);
    return true;
  } catch {
    return false;
  }
}

/** Track a product page view */
export function fbTrackViewContent(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
}) {
  fbqSafe('track', 'ViewContent', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_category: product.category || '',
    content_type: 'product',
    value: product.price || 0,
    currency: 'USD',
  });
}

/** Track add to cart */
export function fbTrackAddToCart(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
  quantity?: number;
}) {
  fbqSafe('track', 'AddToCart', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_type: 'product',
    value: (product.price || 0) * (product.quantity || 1),
    currency: 'USD',
  });
}

/** Track checkout initiation */
export function fbTrackInitiateCheckout(params: {
  value: number;
  numItems: number;
  contentIds?: string[];
}) {
  fbqSafe('track', 'InitiateCheckout', {
    value: params.value,
    currency: 'USD',
    num_items: params.numItems,
    content_ids: params.contentIds || [],
    content_type: 'product',
  });
}

/** Track completed purchase */
export function fbTrackPurchase(params: {
  value: number;
  orderId?: string;
  contentIds?: string[];
  numItems?: number;
}) {
  if (!isFbqAvailable()) return false;
  const value = Number(params.value);
  if (!Number.isFinite(value) || value < 0) return false;
  // eventID = the order number, so a repeat of the same order's Purchase
  // (reload, second tab, or a future Conversions API send using the same id)
  // is deduplicated by Meta instead of counted twice.
  const orderId = params.orderId ? String(params.orderId) : '';
  return fbqSafe('track', 'Purchase', {
    value,
    currency: 'USD',
    content_ids: params.contentIds || [],
    content_type: 'product',
    num_items: params.numItems || 1,
    ...(orderId ? { order_id: orderId } : {}),
  }, ...(orderId ? [{ eventID: orderId }] : []));
}

/** Track search */
export function fbTrackSearch(query: string) {
  fbqSafe('track', 'Search', {
    search_string: query,
  });
}

/** Track lead (e.g., email signup, sample request) */
export function fbTrackLead(params?: { value?: number; category?: string; eventId?: string }) {
  fbqSafe('track', 'Lead', {
    value: params?.value || 0,
    currency: 'USD',
    content_category: params?.category || '',
  }, ...(params?.eventId ? [{ eventID: params.eventId }] : []));
}

/** Track a contact action (e.g. a tap on a phone number). Meta standard event. */
export function fbTrackContact(params?: { method?: string; source?: string }) {
  fbqSafe('track', 'Contact', {
    content_category: params?.method || 'phone',
    content_name: params?.source || '',
  });
}

/** Track custom event */
export function fbTrackCustom(eventName: string, params?: Record<string, any>) {
  // A blank name reaches Meta as "__missing_event"; never send one.
  if (typeof eventName !== 'string' || !eventName.trim()) return;
  fbqSafe('trackCustom', eventName, params || {});
}
